import { FC, useEffect } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { useAuth } from '../../../../../providers/AuthProvider';
import { DigitacaoFormField } from '../../../../../models/SimulationBank/SimulationBankModels';
import FormatMoney from '../../../../../utils/FormatMoney';
import { groupFieldsIntoTabs, getOptionLabel, getNestedValue } from '../StepDigitacao/digitacaoFormUtils';
import { findTipoDocumentoField, getDigitacaoTabLabel, getDocumentoFieldText } from '../StepDigitacao/digitacaoFieldRules';
import TypingAgentField from './TypingAgentField';

const EFETIVACAO_STATUS_MESSAGES = [
    'Enviando os dados da proposta para o banco',
    'Efetivando a proposta junto à financeira',
    'Quase lá!',
];

// AI-1134 — usuários Operacional (user_type.id 1) são contas internas sem partner_code,
// então a checagem de comissionamento abaixo sempre volta "não encontrado" e travava a
// efetivação pra sempre. Esse perfil precisa poder testar a efetivação de propostas
// (produção e homologação) sem passar pela trava de comissionamento/Agente de Digitação.
// Operacional MultiSistema (id 56) ficou de fora: o bypass não se comportava do jeito
// esperado pra esse perfil.
const TYPING_AGENT_CHECK_BYPASS_USER_TYPE_IDS = [1];

const formatDate = (value?: string): string => (value ? moment(value).format('DD/MM/YYYY') : '—');

const Field: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
    <div className="confirmacao-field">
        <dt>{label}</dt>
        <dd>{value != null && value !== '' ? value : '—'}</dd>
    </div>
);

const resolveDisplayValue = (field: DigitacaoFormField, rawValue: any): string | number | null => {
    if (rawValue == null || rawValue === '') return null;
    if (field.type === 'select') return getOptionLabel(field, rawValue);
    if (field.type === 'date') return formatDate(rawValue);
    if (field.type === 'number') return FormatMoney(Number(rawValue));
    return rawValue;
};

const StepConfirmacao: FC<{ onBack: () => void; onConfirmado: () => void }> = ({ onBack, onConfirmado }) => {
    const {
        formDataDigitacao,
        digitacaoForm,
        ofertaSelecionada,
        isSubmittingProposta,
        handleSubmitProposta,
        loggedPartnerCommission,
        isCheckingLoggedCommission,
        checkLoggedPartnerCommission,
        typingAgent,
        isCheckingTypingAgent,
        clearTypingAgent,
    } = useSimulationBank();
    const { userLogged } = useAuth();

    const bypassTypingAgentCheck = TYPING_AGENT_CHECK_BYPASS_USER_TYPE_IDS.includes(Number(userLogged?.user_type?.id));

    // Checa se o parceiro logado é comissionado assim que o espelho é aberto — se não for,
    // libera o campo de agente de digitação (ver TypingAgentField) antes de permitir efetivar.
    // Pulado para os perfis de teste (ver TYPING_AGENT_CHECK_BYPASS_USER_TYPE_IDS).
    useEffect(() => {
        if (!bypassTypingAgentCheck && loggedPartnerCommission == null) checkLoggedPartnerCommission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bypassTypingAgentCheck]);

    // Reseta o agente de digitação validado sempre que sai desta tela (Voltar/Editar ou
    // concluindo a efetivação) — sem isso, o parceiro volta pra Confirmação (ex.: corrigiu
    // algo no formulário e retornou) com o TypingAgentField zerado (campo vazio, componente
    // remontou), mas o nome resolvido de uma passagem anterior continua fixo ao lado,
    // mostrando um agente sem o código correspondente mais digitado.
    useEffect(() => {
        return () => clearTypingAgent();
    }, [clearTypingAgent]);

    const handleConfirmar = async () => {
        await handleSubmitProposta();
        onConfirmado();
    };

    if (isSubmittingProposta) {
        return <LoadingScreen title={<>Estamos efetivando<br />sua proposta!</>} messages={EFETIVACAO_STATUS_MESSAGES} />;
    }

    if (!formDataDigitacao || !ofertaSelecionada || !digitacaoForm) return null;

    const tabs = groupFieldsIntoTabs(digitacaoForm);
    const tipoDocumentoField = findTipoDocumentoField(digitacaoForm);
    const tipoDocumentoValue = tipoDocumentoField ? getNestedValue(formDataDigitacao, tipoDocumentoField.chave) : undefined;
    const partnerNotFound = !bypassTypingAgentCheck && loggedPartnerCommission != null && !loggedPartnerCommission.encontrado;
    const needsTypingAgent = !bypassTypingAgentCheck && loggedPartnerCommission != null && loggedPartnerCommission.encontrado && !loggedPartnerCommission.comissionado;
    const nextDisabled = !bypassTypingAgentCheck && (isCheckingLoggedCommission || partnerNotFound || (needsTypingAgent && (!typingAgent || isCheckingTypingAgent)));

    return (
        <div className="step-results-v2">
            <p className="step-results-v2__lead">
                Confira os dados abaixo antes de efetivar. Se precisar corrigir algo, use o botão Voltar ou Editar.
            </p>

            <div className="confirmacao-card">
                {isCheckingLoggedCommission && (
                    <div className="confirmacao-section confirmacao-agent-check">
                        <FontAwesomeIcon icon={faSpinner} spin /> Verificando comissionamento do parceiro...
                    </div>
                )}

                {partnerNotFound && (
                    <div className="confirmacao-section">
                        <p className="error">Não foi possível identificar o seu código de parceiro. Entre em contato com o suporte antes de efetivar.</p>
                    </div>
                )}

                {needsTypingAgent && (
                    <div className="confirmacao-section">
                        <h5>Agente de Digitação</h5>
                        <p className="step-results-v2__lead" style={{ margin: '-8px 0 16px' }}>
                            Seu parceiro não está comissionado para digitação. Informe o código de outro parceiro comissionado para continuar.
                        </p>
                        <TypingAgentField />
                    </div>
                )}

                {tabs.map((tab) => (
                    <div className="confirmacao-section" key={tab.key}>
                        <h5>{getDigitacaoTabLabel(tab, ofertaSelecionada.financeira)}</h5>
                        <dl className="confirmacao-fields">
                            {tab.fields.map((field) => (
                                <Field
                                    key={field.chave}
                                    label={getDocumentoFieldText(field, tipoDocumentoValue).label}
                                    value={resolveDisplayValue(field, getNestedValue(formDataDigitacao, field.chave))}
                                />
                            ))}
                        </dl>
                    </div>
                ))}

                <div className="confirmacao-section">
                    <h5>Financeira Selecionada</h5>
                    <dl className="confirmacao-fields">
                        <Field label="Financeira" value={ofertaSelecionada.financeira} />
                        <Field label="Valor Solicitado" value={ofertaSelecionada.valor_solicitado != null ? FormatMoney(ofertaSelecionada.valor_solicitado) : '—'} />
                        <Field label="Parcelas" value={ofertaSelecionada.qtde_parcelas ? `${ofertaSelecionada.qtde_parcelas}x` : '—'} />
                    </dl>
                </div>
            </div>

            <StepNavigation
                onBack={onBack}
                onNext={handleConfirmar}
                nextLabel="Digitar a proposta no Banco"
                nextDisabled={nextDisabled}
                extraButton={{ label: 'Editar', onClick: onBack, variant: 'ghost' }}
            />
        </div>
    );
};

export default StepConfirmacao;
